import { useRouter } from "@tanstack/react-router"
import { parseISO } from "date-fns"
import { Button } from "design-system/atoms/Button"
import { Separator } from "design-system/atoms/Separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { ColorConfig } from "design-system/organisms/chat/Chat"
import { Message } from "design-system/organisms/chat/ChatMessage"
import { MessageList } from "design-system/organisms/chat/MessageList"
import { formatDate } from "lopermut"
import { Copy } from "lucide-react"
import { KindMap, RoleMap } from "modules/conversations/lib/maps"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ConversationAmphoraOriginResponseDataSchema, MessageFormatEnum } from "schema"
import { toast } from "sonner"
import { z } from "zod"

type Origin = NonNullable<z.infer<typeof ConversationAmphoraOriginResponseDataSchema>>
type OriginConversation = Origin["conversations"][number]

// Both testing colors are light in either theme, so the text stays dark in both
const ORIGIN_MESSAGE_TEXT_COLOR = "oklch(0.216 0.006 56.043)"
const ORIGIN_COLOR_CONFIG: ColorConfig = {
  user: { backgroundColor: "var(--testing-2)", color: ORIGIN_MESSAGE_TEXT_COLOR },
  assistant: {
    backgroundColor: "var(--testing-3)",
    color: ORIGIN_MESSAGE_TEXT_COLOR,
    opacity: 0.75,
  },
}

type Props = {
  origin: Origin
}

export function AmphoraOriginConversation({ origin }: Props) {
  return (
    <div className="flex flex-col gap-2">
      <span className="px-0 text-sm font-medium text-testing-2">{origin.company.name}</span>
      <div className="flex flex-col gap-3">
        {origin.conversations.map((conversation) => (
          <OriginConversationTranscript key={conversation.id} conversation={conversation} />
        ))}
      </div>
    </div>
  )
}

function OriginConversationTranscript({ conversation }: { conversation: OriginConversation }) {
  const { i18n, t } = useTranslation()
  const { buildLocation } = useRouter()
  const { timeline } = conversation

  const messages = useMemo<Message[]>(
    () =>
      timeline.messages.map((message) => {
        const isUnsupported = message.format === MessageFormatEnum.Unsupported

        return {
          id: message.id,
          role: RoleMap[message.role],
          format: KindMap[message.format],
          kind: message.kind,
          content: isUnsupported ? null : message.content,
          mediaUrl: message.mediaUrl,
          transcription: isUnsupported
            ? t("conversations.chat.unsupportedMessage")
            : message.transcription,
          externalDeletedAt: message.externalDeletedAt,
          createdAt: new Date(message.createdAt),
          readAt: message.readAt ? new Date(message.readAt) : null,
          messageReaction: message.messageReactions?.at(0)?.emoji ?? null,
          authorName: message.employee?.name,
          source: message.source,
          responseToMessageId: message.responseToMessageId,
        }
      }),
    [timeline.messages, t],
  )

  async function copyLink() {
    const link = buildLocation({
      to: "/conversations/list/$customerId",
      params: { customerId: conversation.customerId },
      search: { conversationId: conversation.id, tab: "conversations" },
    }).href

    await navigator.clipboard.writeText([window.location.origin, link].join(""))
    toast.success(t("shared.copySuccess"))
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center gap-2">
        <span className="shrink-0 text-xs text-testing-2">
          {formatDate(parseISO(conversation.createdAt), "Pp")}
        </span>
        <Separator className="flex-1 bg-testing-2/50" />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="size-6 text-testing-2 hover:text-testing-2"
              aria-label={t("conversations.chat.amphoraOrigin.copyLink")}
              onClick={copyLink}
            >
              <Copy className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("conversations.chat.amphoraOrigin.copyLink")}</TooltipContent>
        </Tooltip>
      </div>
      <MessageList
        messages={messages}
        locale={i18n.language}
        colorConfig={ORIGIN_COLOR_CONFIG}
        supportCopy={{
          questionCreated: t("conversations.chat.support.questionCreated"),
          questionSolvedAutomatically: t("conversations.chat.support.questionSolvedAutomatically"),
          ticketCreated: t("conversations.chat.support.ticketCreated"),
          questionSubjectLabel: t("conversations.chat.supportQuestion.subject"),
          questionSummaryLabel: t("conversations.chat.supportQuestion.summary"),
        }}
        replyMessageCopy={(kind) => t(`conversations.chat.replyToMessage.${kind}`)}
        messageNotGeneratedByPermutCopy={t("conversations.chat.messageNotGeneratedByPermut")}
        messageIsWhatsappTemplateCopy={t("conversations.chat.messageIsWhatsappTemplate")}
        messageIsAmphoraCopy={t("conversations.chat.messageSentFromAmphora")}
        messageExternalSenderCopy={t("conversations.chat.messageExternalSender")}
        messageExternalDeletedCopy={t("conversations.chat.messageDeleted")}
      />
    </div>
  )
}
